'use client';

import type { MouseEvent} from 'react';
import { Fragment, useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { useApiMutation } from '@shared/hooks/useApiMutation';
import { useApiQuery } from '@shared/hooks/useApiQuery';

import { getApplicationReviewReportTypes, reportApplicationReview,reportApplicationSuggestion } from '@api/applications/general';

import type { ActivityHandle } from '@ui/uikit/components/common/Activity';
import { Activity } from '@ui/uikit/components/common/Activity';
import { Button } from '@ui/uikit/components/common/Button';
import { useDropdownMenu } from '@ui/uikit/components/common/DropdownMenu';
import Spinner from '@ui/uikit/components/common/Loader/Loader';
import type {
  ReportFormData} from '@ui/uikit/components/common/VotingDropdownMenu';
import {
  VotingDropdownMenuController,
} from '@ui/uikit/components/common/VotingDropdownMenu';
import { Flag } from '@ui/uikit/components/icons/mono/Flag';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

import type {FeedbackType } from '../ApplicationUserFeedback/ApplicationUserFeedbackCarousel';
import { type FeedbackItem } from '../ApplicationUserFeedback/ApplicationUserFeedbackCarousel';

interface ReviewReportingControllerProps {
  applicationSlug: string;
  applicationName: string;
  feedbackType: FeedbackType;
  feedbackItem: FeedbackItem;
}

const ReviewReportingController = ({
  applicationSlug,
  applicationName,
  feedbackType,
  feedbackItem,
}: ReviewReportingControllerProps) => {
  const { user } = useAuth();

  const { data: reportTypes, isLoading: isLoadingReportTypes } = useApiQuery(
    getApplicationReviewReportTypes,
    [],
    {
      enabled: !!user,
    },
  );

  const { mutateAsync: reportSuggestion, isPending: isSuggestionReporting } = useApiMutation(
    reportApplicationSuggestion,
    applicationSlug
  );

  const { mutateAsync: reportReview, isPending: isReviewReporting } = useApiMutation(
    reportApplicationReview,
    applicationSlug
  );

  const [reportSuccess, setReportSuccess] = useState(false);

  const handleSubmit = useCallback(
    async (data: ReportFormData) => {
      try {
        if (feedbackType === 'Suggestion') {
          await reportSuggestion({ suggestionId: feedbackItem.id, ...data });
        } else {
          await reportReview({ reviewId: feedbackItem.id, ...data });
        }

        setReportSuccess(true);
      } catch (error) {
        toast.error(`Failed to submit report. ${getErrorMessage(error)}`);
      }
    },
    [feedbackType, reportSuggestion, feedbackItem.id, reportReview],
  );

  return (
    <VotingDropdownMenuController
      options={reportTypes || []}
      onSubmit={handleSubmit}
      feedbackType={feedbackType}
      isReporting={isReviewReporting || isSuggestionReporting}
      isSuccess={reportSuccess}
      onSuccessHandled={() => setReportSuccess(false)}
    >
      <ReviewReportingControl
        isLoading={isLoadingReportTypes}
        hasMenu={!!reportTypes && reportTypes.length > 0}
        feedbackType={ feedbackType }
        feedbackItem={feedbackItem}
        applicationName={applicationName}
      />
    </VotingDropdownMenuController>
  );
};

const ReviewReportingControl = ({
  isLoading,
  hasMenu,
  applicationName,
  feedbackItem,
  feedbackType,
}: {
  isLoading: boolean;
  hasMenu: boolean;
  applicationName: string;
  feedbackItem: FeedbackItem;
  feedbackType: FeedbackType;
}) => {
  const { openMenu } = useDropdownMenu();

  const { user } = useAuth();

  const { open: openAuthModal } = useAuthModal();

  const button = useRef<HTMLButtonElement & HTMLAnchorElement>(null);

  const handleClick = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation();

      if (!flow.current) {
        console.warn(`Flow handle not initialized.`);

        return;
      }

      if (!button.current) {
        console.warn(`Button handle not initialized.`);

        return;
      }

      await flow.current.initiate();

      if (hasMenu) {
        openMenu(button.current);
      }
    },
    [openMenu, hasMenu],
  );

  const flow = useRef<ActivityHandle>(null);

  const signIn = useCallback(() => {
    openAuthModal();
  }, [openAuthModal]);

  return (
    <Fragment>
      <Activity ref={flow}>
        <Activity.Step requirement={!!user} action={signIn} key="signin" />
      </Activity>
      <Button
        size="small"
        variant="secondary"
        subVariant="ghost"
        onClick={handleClick}
        ref={button}
        label={ `Report ${ feedbackType.toLowerCase() } on ${ applicationName } by ${ feedbackItem.author.name }` }
      >
        {isLoading ? <Spinner size="sm" color="light" /> : <Flag />}
      </Button>
    </Fragment>
  );
};

export { ReviewReportingController, ReviewReportingControl };
